import { apiClient } from "./client";

export interface EntityTotals {
  simulators: number;
  versions: {
    total: number;
    published: number;
    draft: number;
  };
  reference_tables: number;
  variable_groups: number;
  variables: number;
  form_field_groups: number;
  form_fields: number;
  output_fields: number;
  simulations: number;
  shared_simulations: number;
  proposals: number;
  hors_politique_simulations: number;
  simulation_events: number;
  successful_events: number;
  failed_events: number;
  success_rate: number;
  users: number;
  active_users: number;
}

export interface SimulatorDetail {
  id: number;
  name: string;
  slug: string;
  status: string;
  versions_count: number;
  form_fields_count: number;
  output_fields_count: number;
  variables_count: number;
}

export interface EntityRelationship {
  from: string;
  to: string;
  label: string;
}

export interface SimulatorCotationStats {
  simulator_name: string;
  cotations_count: number;
  proposals_count: number;
}

export interface SimulatorEventStats {
  simulator_name: string;
  events_count: number;
}

export interface EntityStatsResponse {
  totals: EntityTotals;
  simulators: SimulatorDetail[];
  relationships: EntityRelationship[];
  cotations_by_simulator: SimulatorCotationStats[];
  simulations_by_simulator: SimulatorEventStats[];
}

export interface EntityStatsParams {
  simulator_id?: number;
  user_id?: number;
  status?: string;
  date_from?: string;
  date_to?: string;
}

export async function getEntityStats(params: EntityStatsParams = {}): Promise<EntityStatsResponse> {
  const response = await apiClient.get<EntityStatsResponse>("/stats/entity-stats", { params });
  return response.data;
}

// Activity types
export interface ActivityUser {
  id: number;
  full_name: string;
  email: string;
}

export interface Activity {
  id: number;
  action: string;
  action_label: string;
  entity_type: string;
  entity_type_label: string;
  entity_id: number;
  entity_name: string;
  entity_link: string | null;
  description: string | null;
  created_at: string;
  user: ActivityUser | null;
}

export interface RecentActivityParams {
  limit?: number;
  date_from?: string;
  date_to?: string;
  simulator_id?: number;
  user_id?: number;
}

export async function getRecentActivity(
  params: RecentActivityParams = {},
): Promise<Activity[]> {
  const { limit = 10, ...rest } = params;
  const response = await apiClient.get<Activity[]>("/activity/recent", {
    params: { limit, ...rest },
  });
  return response.data;
}

// Timeline
export interface TimelinePoint {
  period: string;
  simulations: number;
  cotations: number;
  proposals: number;
}

export interface TimelineParams {
  granularity?: "day" | "week" | "month";
  simulator_id?: number;
  user_id?: number;
  date_from?: string;
  date_to?: string;
}

export async function getActivityTimeline(params: TimelineParams = {}): Promise<TimelinePoint[]> {
  const response = await apiClient.get<TimelinePoint[]>("/stats/activity-timeline", { params });
  return response.data;
}

// Funnel
export interface FunnelStep {
  label: string;
  count: number;
}

export interface FunnelRate {
  from: string;
  to: string;
  rate: number;
}

export interface FunnelResponse {
  steps: FunnelStep[];
  rates: FunnelRate[];
}

export async function getFunnel(params: EntityStatsParams = {}): Promise<FunnelResponse> {
  const response = await apiClient.get<FunnelResponse>("/stats/funnel", { params });
  return response.data;
}

// Top users
export interface TopUser {
  user_id: number;
  full_name: string;
  email: string;
  cotations: number;
  proposals: number;
  last_activity: string | null;
}

export interface TopUsersParams {
  simulator_id?: number;
  date_from?: string;
  date_to?: string;
  limit?: number;
}

export async function getTopUsers(params: TopUsersParams = {}): Promise<TopUser[]> {
  const response = await apiClient.get<TopUser[]>("/stats/top-users", { params });
  return response.data;
}

// Agency stats
export interface AgencyStat {
  agency_id: number;
  agency_name: string;
  group_name: string | null;
  cotations: number;
  proposals: number;
}

export async function getAgencyStats(params: EntityStatsParams = {}): Promise<AgencyStat[]> {
  const response = await apiClient.get<AgencyStat[]>("/stats/agency-stats", { params });
  return response.data;
}

// Offer × region stats
export interface OfferAgencyStat {
  agency_id: number;
  agency_name: string;
  cotations: number;
  proposals: number;
}

export interface OfferRegionStat {
  /** null = cotations sans agence (« Non renseignée ») */
  group_id: number | null;
  group_name: string | null;
  cotations: number;
  proposals: number;
  agencies: OfferAgencyStat[];
}

export interface OfferStat {
  simulator_id: number;
  simulator_name: string;
  simulations: number;
  cotations: number;
  proposals: number;
  regions: OfferRegionStat[];
}

export async function getOfferRegionStats(params: EntityStatsParams = {}): Promise<OfferStat[]> {
  const response = await apiClient.get<OfferStat[]>("/stats/offer-region-stats", { params });
  return response.data;
}

// User activity
export interface UserActivity {
  user_id: number;
  full_name: string;
  email: string;
  role: string;
  is_active: boolean;
  last_login: string | null;
  simulation_events: number;
  cotations: number;
  proposals: number;
  last_activity: string | null;
}

export interface UserActivityParams {
  simulator_id?: number;
  date_from?: string;
  date_to?: string;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  only_connected?: boolean;
  page?: number;
  page_size?: number;
}

export interface UserActivityResponse {
  items: UserActivity[];
  total_items: number;
  total_pages: number;
  current_page: number;
  page_size: number;
}

export async function getUserActivity(params: UserActivityParams = {}): Promise<UserActivityResponse> {
  const response = await apiClient.get<UserActivityResponse>("/stats/user-activity", { params });
  return response.data;
}

/**
 * Tous les comptes répondant aux filtres, page après page. La liste est plafonnée à
 * 100 comptes par page : un seul appel en perdrait silencieusement au-delà.
 */
export async function getAllUserActivity(
  params: Omit<UserActivityParams, "page" | "page_size"> = {}
): Promise<UserActivity[]> {
  const first = await getUserActivity({ ...params, page: 1, page_size: 100 });
  const items = [...first.items];
  for (let page = 2; page <= first.total_pages; page++) {
    const next = await getUserActivity({ ...params, page, page_size: 100 });
    items.push(...next.items);
  }
  return items;
}

